import { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Polyline, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-polylineoffset';
import { getCourseColor } from './CourseListPanel.jsx';
import { DIRECTION_BEARINGS, DIRECTION_LETTERS, courseDisplayLabel } from '../lib/courseConstants.js';
import { distanceNm, bearingDeg, offsetLineBearing, fixedMarkLineBearing } from '../lib/geo.js';
import { buildTrackSegments, trackHasSpeed, SPEED_LEGEND } from '../lib/trackOverlay.js';
import { BASE_TILE_LAYER, SEAMARK_TILE_LAYER } from '../lib/basemap.js';

const LINE_WEIGHT = 3;

/** Parse mark appearance string to a display color. */
function markColor(appearance) {
  const a = (appearance || '').toLowerCase();
  if (a.includes('red') || a.startsWith('r')) return '#dc2626';
  if (a.includes('green') || a.startsWith('g')) return '#16a34a';
  if (a.includes('yellow') || a.startsWith('y')) return '#d97706';
  if (a.includes('black') || a.startsWith('b')) return '#1a1a2e';
  if (a.includes('orange')) return '#ea580c';
  return '#6b7280';
}

function hasCoords(mark) {
  return (
    typeof mark.latitude === 'number' &&
    typeof mark.longitude === 'number' &&
    !(mark.latitude === 0 && mark.longitude === 0)
  );
}

function buildMarkLookup(marks) {
  const lookup = {};
  for (const m of marks) {
    if (hasCoords(m)) lookup[m.id] = [m.latitude, m.longitude];
  }
  return lookup;
}

/** Normalize a mark pair key so A→B and B→A produce the same key. */
function segmentKey(markA, markB) {
  return markA < markB ? `${markA}|${markB}` : `${markB}|${markA}`;
}

function projectPoint(lat, lng, bearingDegs, distanceNmVal) {
  const nmPerDegLat = 60;
  const nmPerDegLng = 60 * Math.cos(lat * Math.PI / 180);
  const rad = bearingDegs * Math.PI / 180;
  return [
    lat + distanceNmVal * Math.cos(rad) / nmPerDegLat,
    lng + distanceNmVal * Math.sin(rad) / nmPerDegLng,
  ];
}

/**
 * Auto-fit map bounds to visible marks (or focused course marks) plus any
 * loaded track. Including the track lets an admin frame a GPS track even when
 * the course isn't known and few/no marks are in view.
 */
function FitBounds({ marks, focusedMarkIds, track }) {
  const map = useMap();
  const geoMarks = useMemo(() => {
    if (focusedMarkIds && focusedMarkIds.length > 0) {
      const idSet = new Set(focusedMarkIds);
      return marks.filter((m) => idSet.has(m.id) && hasCoords(m));
    }
    return marks.filter(hasCoords);
  }, [marks, focusedMarkIds]);

  useEffect(() => {
    const bounds = geoMarks.map((m) => [m.latitude, m.longitude]);
    // When not focused on a specific course, frame the track too.
    if (!focusedMarkIds && track && track.length > 0) {
      for (const p of track) bounds.push([p[0], p[1]]);
    }
    if (bounds.length === 0) return;
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  }, [geoMarks, track, focusedMarkIds, map]);
  return null;
}

/**
 * Build per-segment offset data for all selected courses.
 */
function buildSegmentOffsets(courseLines) {
  const segmentGroups = {};

  for (const { card } of courseLines) {
    const ids = card.markIds;
    for (let i = 0; i < ids.length - 1; i++) {
      const key = segmentKey(ids[i], ids[i + 1]);
      if (!segmentGroups[key]) segmentGroups[key] = [];
      segmentGroups[key].push({ courseId: card.id, segIdx: i });
    }
  }

  const offsets = new Map();
  for (const { card } of courseLines) {
    offsets.set(card.id, []);
  }

  for (const group of Object.values(segmentGroups)) {
    const n = group.length;
    group.forEach((entry, rank) => {
      const px = (rank - (n - 1) / 2) * LINE_WEIGHT;
      const arr = offsets.get(entry.courseId);
      if (arr) arr[entry.segIdx] = px;
    });
  }

  return offsets;
}

/**
 * Dashed line extending from a mark in a given bearing direction.
 * Used for both start (downwind) and finish (upwind) legs.
 */
function DashedLeg({ position, bearing, distNm, color, offsetPx }) {
  if (!position || typeof bearing !== 'number' || !distNm) return null;
  const end = projectPoint(position[0], position[1], bearing, distNm);
  return (
    <Polyline
      positions={[position, end]}
      pathOptions={{
        color,
        weight: LINE_WEIGHT,
        dashArray: '8,5',
        opacity: 0.8,
        offset: offsetPx,
      }}
    />
  );
}

/** Invisible wider polyline for easier hover/click on thin legs. */
function LegHitArea({ posA, posB, offsetPx, card, legIndex, onFocus }) {
  const dist = distanceNm(posA[0], posA[1], posB[0], posB[1]);
  const brg = bearingDeg(posA[0], posA[1], posB[0], posB[1]);

  return (
    <Polyline
      positions={[posA, posB]}
      pathOptions={{
        color: 'transparent',
        weight: 16,
        opacity: 0,
        offset: offsetPx,
      }}
      eventHandlers={{
        click: () => onFocus && onFocus(card.id),
      }}
    >
      <Tooltip sticky>
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {courseDisplayLabel(card)} Leg {legIndex + 1}
          <br />
          {card.markIds[legIndex]} → {card.markIds[legIndex + 1]}
          <br />
          {dist.toFixed(2)} nm / {Math.round(brg)}°
        </span>
      </Tooltip>
    </Polyline>
  );
}

/** Direction arrow marker at leg midpoint. */
function DirectionArrow({ posA, posB, color, opacity }) {
  const midLat = (posA[0] + posB[0]) / 2;
  const midLng = (posA[1] + posB[1]) / 2;
  const brg = bearingDeg(posA[0], posA[1], posB[0], posB[1]);
  const arrowLen = 0.04;
  const rad = brg * Math.PI / 180;

  const tipLat = midLat + arrowLen * Math.cos(rad) / 60;
  const tipLng = midLng + arrowLen * Math.sin(rad) / (60 * Math.cos(midLat * Math.PI / 180));

  const flankLen = arrowLen * 0.8;
  const spread = 2.6;
  const leftRad = rad + spread;
  const rightRad = rad - spread;

  const leftLat = tipLat + flankLen * Math.cos(leftRad) / 60;
  const leftLng = tipLng + flankLen * Math.sin(leftRad) / (60 * Math.cos(midLat * Math.PI / 180));
  const rightLat = tipLat + flankLen * Math.cos(rightRad) / 60;
  const rightLng = tipLng + flankLen * Math.sin(rightRad) / (60 * Math.cos(midLat * Math.PI / 180));

  return (
    <Polyline
      positions={[
        [leftLat, leftLng],
        [tipLat, tipLng],
        [rightLat, rightLng],
      ]}
      pathOptions={{
        color,
        weight: 2,
        opacity,
        fill: false,
      }}
    />
  );
}

/** 16-point wind rose SVG showing active direction sectors. */
function WindRose({ activeDirections }) {
  const size = 90;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = 38;
  const sectorHalf = 360 / 16 / 2;
  const activeSet = new Set(activeDirections);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={outerR + 2} fill="rgba(255,255,255,0.85)" stroke="#ccc" strokeWidth="1" />
      {DIRECTION_LETTERS.map((letter) => {
        if (!activeSet.has(letter)) return null;
        const dirBearing = DIRECTION_BEARINGS[letter];
        const startAngle = (dirBearing - sectorHalf - 90) * Math.PI / 180;
        const endAngle = (dirBearing + sectorHalf - 90) * Math.PI / 180;
        const x1 = cx + outerR * Math.cos(startAngle);
        const y1 = cy + outerR * Math.sin(startAngle);
        const x2 = cx + outerR * Math.cos(endAngle);
        const y2 = cy + outerR * Math.sin(endAngle);
        return (
          <path
            key={letter}
            d={`M${cx},${cy} L${x1},${y1} A${outerR},${outerR} 0 0,1 ${x2},${y2} Z`}
            fill="rgba(99,102,241,0.25)"
            stroke="rgba(99,102,241,0.5)"
            strokeWidth="0.5"
          />
        );
      })}
      {DIRECTION_LETTERS.map((letter) => {
        const dirBearing = DIRECTION_BEARINGS[letter];
        const angle = (dirBearing - 90) * Math.PI / 180;
        const r1 = outerR - 4;
        return (
          <line
            key={letter}
            x1={cx + r1 * Math.cos(angle)}
            y1={cy + r1 * Math.sin(angle)}
            x2={cx + outerR * Math.cos(angle)}
            y2={cy + outerR * Math.sin(angle)}
            stroke="#666"
            strokeWidth="1"
          />
        );
      })}
      {[
        { label: 'N', angle: -90 },
        { label: 'E', angle: 0 },
        { label: 'S', angle: 90 },
        { label: 'W', angle: 180 },
      ].map(({ label, angle }) => {
        const r = outerR - 11;
        const rad = angle * Math.PI / 180;
        return (
          <text
            key={label}
            x={cx + r * Math.cos(rad)}
            y={cy + r * Math.sin(rad)}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="10"
            fontWeight="700"
            fill="#333"
          >
            {label}
          </text>
        );
      })}
      <circle cx={cx} cy={cy} r={2} fill="#666" />
    </svg>
  );
}

/** Format a track point's timestamp (HH:MM:SS) for a tooltip. */
function fmtPointTime(ts) {
  if (!ts) return null;
  // Match the analysis page: treat naive timestamps as UTC.
  const d = new Date(ts + (ts.includes('+') || ts.includes('Z') ? '' : 'Z'));
  if (Number.isNaN(d.getTime())) return ts;
  return d.toISOString().slice(11, 19);
}

/** Tooltip body for a track endpoint dot. */
function TrackPointTooltip({ label, point }) {
  const time = fmtPointTime(point[2]);
  const speed = point[3];
  return (
    <Tooltip direction="top" offset={[0, -6]}>
      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
        <strong>{label}</strong>
        {time && <><br />{time}</>}
        {speed !== null && speed !== undefined && <><br />{speed.toFixed(1)} kn</>}
      </span>
    </Tooltip>
  );
}

/**
 * Render a boat GPS track: speed-coloured (or solid) polyline plus start/finish
 * dots. `track` is a list of [lat, lon, ts?, speed?] fixes.
 */
function TrackOverlay({ track }) {
  const segments = useMemo(() => buildTrackSegments(track), [track]);
  if (!track || track.length === 0) return null;

  const start = track[0];
  const finish = track[track.length - 1];

  return (
    <>
      {segments.map((seg, i) => (
        <Polyline
          key={`track-seg-${i}`}
          positions={seg.positions}
          pathOptions={{ color: seg.color, weight: 3, opacity: 0.85 }}
        />
      ))}
      <CircleMarker
        center={[start[0], start[1]]}
        radius={6}
        pathOptions={{ fillColor: '#16a34a', fillOpacity: 1, color: '#fff', weight: 2 }}
      >
        <TrackPointTooltip label="Track start" point={start} />
      </CircleMarker>
      {track.length > 1 && (
        <CircleMarker
          center={[finish[0], finish[1]]}
          radius={6}
          pathOptions={{ fillColor: '#dc2626', fillOpacity: 1, color: '#fff', weight: 2 }}
        >
          <TrackPointTooltip label="Track finish" point={finish} />
        </CircleMarker>
      )}
    </>
  );
}

export default function CourseMap({ marks, selectedCards, allCards, focusedId, onFocus, startNm = 0.5, finishNm = 0.25, track = [] }) {
  const geoMarks = useMemo(() => marks.filter(hasCoords), [marks]);
  const lookup = useMemo(() => buildMarkLookup(marks), [marks]);

  const center = useMemo(() => {
    if (geoMarks.length > 0) {
      const lat = geoMarks.reduce((s, m) => s + m.latitude, 0) / geoMarks.length;
      const lng = geoMarks.reduce((s, m) => s + m.longitude, 0) / geoMarks.length;
      return [lat, lng];
    }
    // No marks but a track loaded → centre on its first fix.
    if (track.length > 0) return [track[0][0], track[0][1]];
    return [53.3, -6.15];
  }, [geoMarks, track]);

  const trackSpeedColoured = useMemo(() => trackHasSpeed(track), [track]);

  const cardIndexMap = useMemo(() => {
    const m = {};
    allCards.forEach((c, i) => { m[c.id] = i; });
    return m;
  }, [allCards]);

  const courseLines = useMemo(() => {
    return selectedCards.map((card) => {
      const positions = card.markIds.map((id) => lookup[id]).filter(Boolean);
      const roundings = card.markDirections || [];
      const colorIdx = cardIndexMap[card.id] ?? 0;
      return { card, positions, roundings, color: getCourseColor(colorIdx) };
    });
  }, [selectedCards, lookup, cardIndexMap]);

  const segmentOffsets = useMemo(() => buildSegmentOffsets(courseLines), [courseLines]);

  // Start (downwind) and finish (upwind) dashed legs, grouped for pixel offset
  const dashedLegs = useMemo(() => {
    const startGroups = {};
    const finishGroups = {};
    for (const { card, positions, color } of courseLines) {
      if (positions.length === 0) continue;
      const windBearing = DIRECTION_BEARINGS[card.directionCode];
      const lastIdx = positions.length - 1;
      // Honor the configured start/finish offset ANGLE (a numeric bearing in
      // published bundles, or the downwind/upwind default when unset) instead
      // of always drawing the wind-derived default. Matches the editor leg
      // table and the app's live start/finish placement. (ra-etwi)
      //
      // 'fixed_mark' lines sit ON a single fixed mark (markA) and are oriented
      // by a numeric offsetAngle or the course's approach/departure direction,
      // so they render even when no wind/direction is configured. (ra-gbrd.22)
      if (card.startType === 'fixed_mark') {
        const anchor = lookup[card.startMarkA];
        const numeric = Number(card.startOffsetAngle);
        const startBearing = Number.isFinite(numeric)
          ? ((numeric % 360) + 360) % 360
          : fixedMarkLineBearing(card.markIds, lookup, card.startMarkA, true);
        if (anchor && startBearing != null) {
          const startKey = `${card.startMarkA}-${startBearing}`;
          if (!startGroups[startKey]) startGroups[startKey] = [];
          startGroups[startKey].push({ position: anchor, bearing: startBearing, color, type: 'start' });
        }
      } else if (typeof windBearing === 'number') {
        const startBearing = offsetLineBearing(card.startOffsetAngle, windBearing, true);
        const startKey = `${card.markIds[0]}-${startBearing}`;
        if (!startGroups[startKey]) startGroups[startKey] = [];
        startGroups[startKey].push({ position: positions[0], bearing: startBearing, color, type: 'start' });
      }
      if (card.finishType === 'fixed_mark') {
        const anchor = lookup[card.finishMarkA];
        const numeric = Number(card.finishOffsetAngle);
        const finishBearing = Number.isFinite(numeric)
          ? ((numeric % 360) + 360) % 360
          : fixedMarkLineBearing(card.markIds, lookup, card.finishMarkA, false);
        if (anchor && finishBearing != null) {
          const finishKey = `${card.finishMarkA}-${finishBearing}`;
          if (!finishGroups[finishKey]) finishGroups[finishKey] = [];
          finishGroups[finishKey].push({ position: anchor, bearing: finishBearing, color, type: 'finish' });
        }
      } else if (typeof windBearing === 'number') {
        const finishBearing = offsetLineBearing(card.finishOffsetAngle, windBearing, false);
        const lastMarkId = card.markIds[card.markIds.length - 1];
        const finishKey = `${lastMarkId}-${finishBearing}`;
        if (!finishGroups[finishKey]) finishGroups[finishKey] = [];
        finishGroups[finishKey].push({ position: positions[lastIdx], bearing: finishBearing, color, type: 'finish' });
      }
    }
    const legs = [];
    for (const group of Object.values(startGroups)) {
      group.forEach((leg, i) => {
        const px = (i - (group.length - 1) / 2) * LINE_WEIGHT;
        legs.push({ ...leg, offsetPx: px, distNm: startNm });
      });
    }
    for (const group of Object.values(finishGroups)) {
      group.forEach((leg, i) => {
        const px = (i - (group.length - 1) / 2) * LINE_WEIGHT;
        legs.push({ ...leg, offsetPx: px, distNm: finishNm });
      });
    }
    return legs;
  }, [courseLines, startNm, finishNm]);

  const activeDirections = useMemo(() => {
    const dirs = new Set();
    for (const card of selectedCards) {
      if (card.directionCode) dirs.add(card.directionCode);
    }
    return Array.from(dirs);
  }, [selectedCards]);

  const focusedMarkIds = useMemo(() => {
    if (!focusedId) return null;
    const card = selectedCards.find((c) => c.id === focusedId);
    return card ? card.markIds : null;
  }, [focusedId, selectedCards]);

  const focusedMarkSet = useMemo(() => {
    if (!focusedMarkIds) return null;
    return new Set(focusedMarkIds);
  }, [focusedMarkIds]);

  // Keep showing the map when a track is loaded even if no marks have coords,
  // so an admin can frame the track against (still-unplaced) candidate courses.
  if (geoMarks.length === 0 && track.length === 0) {
    return (
      <div className="map-empty">
        <p>No marks with geographic coordinates.</p>
        <p>Marks need latitude/longitude data to display on the map.</p>
      </div>
    );
  }

  return (
    <div className="map-container">
      {activeDirections.length > 0 && (
        <div className="wind-rose-overlay">
          <WindRose activeDirections={activeDirections} />
        </div>
      )}
      {track.length > 0 && trackSpeedColoured && (
        <div className="track-legend-overlay">
          <div className="track-legend-title">Boat speed</div>
          {SPEED_LEGEND.map(([color, label]) => (
            <div key={label} className="track-legend-row">
              <span className="track-legend-swatch" style={{ background: color }} />
              {label}
            </div>
          ))}
        </div>
      )}
      <MapContainer center={center} zoom={13} className="course-map">
        <TileLayer {...BASE_TILE_LAYER} />
        <TileLayer {...SEAMARK_TILE_LAYER} />
        <FitBounds marks={marks} focusedMarkIds={focusedMarkIds} track={track} />

        {/* Mark dots */}
        {geoMarks.map((m) => {
          const dimmed = focusedMarkSet && !focusedMarkSet.has(m.id);
          return (
            <CircleMarker
              key={m.id}
              center={[m.latitude, m.longitude]}
              radius={dimmed ? 4 : 6}
              pathOptions={{
                fillColor: markColor(m.appearance),
                fillOpacity: dimmed ? 0.3 : 0.9,
                color: '#fff',
                weight: dimmed ? 1 : 2,
              }}
            >
              <Tooltip direction="top" offset={[0, -8]}>
                <strong>{m.id}</strong> {m.name || ''}
              </Tooltip>
            </CircleMarker>
          );
        })}

        {/* Course segments — one polyline per leg, with pixel offset */}
        {courseLines.map(({ card, color }) => {
          const offsets = segmentOffsets.get(card.id) || [];
          const ids = card.markIds;
          const roundings = card.markDirections || [];
          const isFocused = focusedId === card.id;
          const isDimmed = focusedId && !isFocused;
          const lineOpacity = isDimmed ? 0.2 : 0.8;
          const lineWeight = isFocused ? LINE_WEIGHT + 2 : LINE_WEIGHT;
          const elements = [];

          for (let i = 0; i < ids.length - 1; i++) {
            const posA = lookup[ids[i]];
            const posB = lookup[ids[i + 1]];
            if (!posA || !posB) continue;

            elements.push(
              <Polyline
                key={`${card.id}-seg-${i}`}
                positions={[posA, posB]}
                pathOptions={{
                  color,
                  weight: lineWeight,
                  opacity: lineOpacity,
                  offset: offsets[i] || 0,
                }}
              />
            );

            if (!isDimmed) {
              elements.push(
                <DirectionArrow
                  key={`${card.id}-arr-${i}`}
                  posA={posA}
                  posB={posB}
                  color={color}
                  opacity={lineOpacity}
                />
              );
            }

            elements.push(
              <LegHitArea
                key={`${card.id}-hit-${i}`}
                posA={posA}
                posB={posB}
                offsetPx={offsets[i] || 0}
                card={card}
                legIndex={i}
                onFocus={onFocus}
              />
            );
          }

          ids.forEach((id, i) => {
            const pos = lookup[id];
            if (!pos || i >= roundings.length) return;
            const isPort = roundings[i] === 'port';
            elements.push(
              <CircleMarker
                key={`${card.id}-r-${id}-${i}`}
                center={pos}
                radius={isDimmed ? 2 : 3}
                pathOptions={{
                  fillColor: isPort ? '#dc2626' : '#16a34a',
                  fillOpacity: isDimmed ? 0.2 : 1,
                  color: isPort ? '#dc2626' : '#16a34a',
                  weight: 1,
                }}
              />
            );
          });

          return elements;
        })}

        {/* Start (downwind) and finish (upwind) dashed legs */}
        {dashedLegs.map((leg, i) => (
          <DashedLeg
            key={`dash-${leg.type}-${i}`}
            position={leg.position}
            bearing={leg.bearing}
            distNm={leg.distNm}
            color={leg.color}
            offsetPx={leg.offsetPx}
          />
        ))}

        {/* Boat GPS track overlay */}
        <TrackOverlay track={track} />
      </MapContainer>
    </div>
  );
}
